import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FaHotel,
  FaUtensils,
  FaStore,
  FaCheck,
  FaEye,
  FaEyeSlash,
  FaPlus,
  FaTrash,
  FaChevronDown,
  FaChevronUp,
  FaEdit,
  FaLock,
  FaArrowRight,
} from "react-icons/fa";
import {
  registerOwner,
  verifyOwnerEmail,
  resendOwnerOtp,
  sendWhatsAppOtp,
  verifyWhatsAppOtp,
  getSubscriptionPlans,
  getSubscriptionDurations,
  getPricingPlans,
  getShopCategories,
  createMultipleBusinessOnboarding,
  createRazorpayOrder,
  verifyRazorpayPayment,
  getOwnerOnboardingProgress,
  RAZORPAY_KEY_ID,
} from "../services/onboardingApi";
import { useAuth } from "../AuthContext";

// ─── Constants ───────────────────────────────────────────────────────────────

const MONTHS_IN_YEAR = 12;

const STEPS = [
  { label: "Account", key: "account" },
  { label: "Verify", key: "verify" },
  { label: "Details", key: "details" },
  { label: "Business", key: "business" },
  { label: "Plans", key: "plans" },
  { label: "Review", key: "review" },
  { label: "Payment", key: "payment" },
  { label: "Done", key: "done" },
];

const BUNDLE_DEFINITIONS = [
  { id: "bundle-pos-basic", name: "POS Billing Basic", description: "POS bundle with plans 5 and 7.", planIds: [5, 7], percentage: 83.47 },
  { id: "bundle-pos-premium", name: "POS Billing Premium", description: "POS premium bundle with plans 5, 6, 7 and 8.", planIds: [5, 6, 7, 8], percentage: 83.47 },
  { id: "bundle-ota-hotels", name: "OTA's Software for Hotels and Resorts", description: "Non-POS hotel bundle with plans 1 and 2.", planIds: [1, 2], percentage: 42.97 },
  { id: "bundle-ota-restaurant-qr", name: "OTA's and Digital QR Menu for Restaurants", description: "Non-POS restaurant bundle with plans 3 and 4.", planIds: [3, 4], percentage: 42.97 },
];

const COUPONS = { TRIVANA10: 10, TRIVANA20: 20 };

const HOTEL_FEATURES = ["Swimming Pool", "WiFi", "Restaurant", "Parking", "Conference Hall", "Room Service"];

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatCurrency(amount) {
  return "₹" + Number(amount).toLocaleString("en-IN");
}

function parsePrice(price) {
  if (typeof price === "number") return price;
  if (typeof price === "string") return parseFloat(price.replace(/[^0-9.]/g, "")) || 0;
  return 0;
}

function getPasswordStrength(password) {
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  return score;
}

function strengthLabel(score) {
  if (score <= 1) return { text: "Weak", color: "bg-red-500" };
  if (score <= 2) return { text: "Fair", color: "bg-orange-500" };
  if (score <= 3) return { text: "Good", color: "bg-yellow-500" };
  if (score <= 4) return { text: "Strong", color: "bg-green-500" };
  return { text: "Very Strong", color: "bg-emerald-500" };
}

function calculateTotal(selectedPlans, selectedBundles, pricingPlans, selectedDuration, couponCode) {
  let base = 0;

  // Bundle pricing: percentage of yearly total
  if (selectedBundles.length > 0) {
    selectedBundles.forEach((bundle) => {
      const bundleDef = BUNDLE_DEFINITIONS.find((b) => b.id === bundle);
      if (!bundleDef) return;
      let yearlyTotal = 0;
      bundleDef.planIds.forEach((planId) => {
        const plan = pricingPlans.find((p) => p.planId === planId || p.id === planId);
        if (plan) yearlyTotal += parsePrice(plan.basePrice) * MONTHS_IN_YEAR;
      });
      base += (bundleDef.percentage / 100) * yearlyTotal;
    });
  }

  // Pack pricing removed as requested

  const total = base;
  let discountPercent = 0;
  if (couponCode && COUPONS[couponCode.toUpperCase()]) {
    discountPercent = COUPONS[couponCode.toUpperCase()];
  }
  const discounted = total - (total * discountPercent) / 100;
  const grandTotal = discounted;

  return { base, total, discounted, grandTotal };
}

// ─── Animation Variants ──────────────────────────────────────────────────────

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
  exit: { opacity: 0, y: -20, transition: { duration: 0.3, ease: "easeIn" } },
};

const buttonHover = { scale: 1.02, transition: { duration: 0.2 } };
const buttonTap = { scale: 0.98 };

// ─── Top Panel Component (Desktop) ─────────────────────────────────────────────

function TopPanel({ currentStep }) {
  return (
    <div className="hidden lg:block w-full bg-[#0a0f1c] py-6 border-b border-gray-800">
      <div className="max-w-7xl mx-auto px-6 flex justify-center">
        <nav className="flex items-center">
          {STEPS.map((step, index) => {
            const isCompleted = index < currentStep;
            const isCurrent = index === currentStep;

            return (
              <div key={step.key} className="flex items-center">
                <div className={`flex items-center gap-3 transition-all duration-300`}>
                  {/* Step indicator */}
                  <div className={`relative w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 ${
                    isCompleted
                      ? 'bg-white text-black'
                      : isCurrent
                      ? 'bg-[#334155] text-white'
                      : 'bg-[#1e293b] text-gray-500'
                  }`}>
                    {isCompleted ? <FaCheck className="w-3.5 h-3.5" /> : index + 1}
                  </div>

                  {/* Label */}
                  <span className={`text-sm font-medium transition-all duration-300 ${
                    isCompleted ? 'text-white' : isCurrent ? 'text-white' : 'text-gray-500'
                  }`}>
                    {step.label}
                  </span>
                </div>

                {/* Connecting line */}
                {index < STEPS.length - 1 && (
                  <div className={`w-12 xl:w-20 h-[1px] mx-4 transition-colors duration-300 ${
                    isCompleted ? 'bg-white/40' : 'bg-gray-800'
                  }`} />
                )}
              </div>
            );
          })}
        </nav>
      </div>
    </div>
  );
}

// ─── Mobile Step Indicator ───────────────────────────────────────────────────

function MobileStepIndicator({ currentStep }) {
  return (
    <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-white/95 backdrop-blur-sm sticky top-16 z-30">
      <div className="flex items-center gap-3">
        <div className="flex -space-x-1">
          {STEPS.map((_, i) => (
            <div key={i} className={`w-2 h-2 rounded-full border border-white ${
              i < currentStep ? 'bg-emerald-500' : i === currentStep ? 'bg-blue-500' : 'bg-gray-200'
            }`} />
          ))}
        </div>
        <span className="text-sm font-medium text-gray-700">{STEPS[currentStep]?.label}</span>
      </div>
      <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full">
        {currentStep + 1}/{STEPS.length}
      </span>
    </div>
  );
}

// ─── Reusable Form Components ────────────────────────────────────────────────

function FormCard({ children, className = "" }) {
  return (
    <div className={className}>
      {children}
    </div>
  );
}

function InputField({ label, type = "text", value, onChange, placeholder, required, error, disabled, maxLength, className = "" }) {
  return (
    <div className={`mb-4 sm:mb-5 ${className}`}>
      <label className="block text-[12px] sm:text-[13px] font-medium text-gray-500 mb-1 sm:mb-1.5 uppercase tracking-wide">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        className={`w-full px-0 py-2 sm:py-2.5 bg-transparent border-0 border-b-2 text-sm sm:text-base ${
          error ? "border-red-300 focus:border-red-500" : "border-gray-200 focus:border-blue-500"
        } focus:outline-none focus:ring-0 transition-colors duration-200 text-gray-800 placeholder:text-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed`}
      />
      {error && <p className="text-red-500 text-[11px] sm:text-xs mt-1">{error}</p>}
    </div>
  );
}

function SelectField({ label, value, onChange, options, required, error, placeholder, className = "" }) {
  return (
    <div className={`mb-4 sm:mb-5 ${className}`}>
      <label className="block text-[12px] sm:text-[13px] font-medium text-gray-500 mb-1 sm:mb-1.5 uppercase tracking-wide">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <select
        value={value}
        onChange={onChange}
        className={`w-full px-0 py-2 sm:py-2.5 bg-transparent border-0 border-b-2 text-sm sm:text-base ${
          error ? "border-red-300 focus:border-red-500" : "border-gray-200 focus:border-blue-500"
        } focus:outline-none focus:ring-0 transition-colors duration-200 text-gray-800 appearance-none cursor-pointer`}
      >
        <option value="" className="text-gray-400">{placeholder || "Select..."}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {error && <p className="text-red-500 text-[11px] sm:text-xs mt-1">{error}</p>}
    </div>
  );
}

// ─── SessionStorage Persistence Key ──────────────────────────────────────────
const STORAGE_KEY = 'trivana_onboarding_draft';

function loadSavedState() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function saveState(data) {
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch {}
}

function clearSavedState() {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
}

// ─── Gate Screen (for non-logged-in users) ───────────────────────────────────

function OnboardingGate() {
  const navigate = useNavigate();
  return (
    <div className="min-h-[calc(100vh-68px)] bg-white flex">
      {/* Right gate card */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md text-center"
        >
          {/* Lock icon */}
          <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-6">
            <FaLock className="text-blue-500 text-2xl" />
          </div>

          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3" style={{ fontFamily: "Montserrat, sans-serif" }}>
            Sign in to start your business journey
          </h1>
          <p className="text-gray-500 text-sm mb-8 leading-relaxed">
            Create a free account or sign in to access the onboarding form and set up your hospitality business on Trivana.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <motion.button
              whileHover={{ scale: 1.03, boxShadow: "0 8px 25px rgba(37,99,235,0.3)" }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate("/login?mode=signup&redirect=/onboarding")}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-3.5 bg-blue-600 text-white rounded-xl font-semibold text-sm hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20"
              style={{ fontFamily: "Montserrat, sans-serif" }}
            >
              Create Free Account
              <FaArrowRight className="w-3 h-3" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate("/login?redirect=/onboarding")}
              className="flex-1 sm:flex-none px-6 py-3.5 border-2 border-gray-200 text-gray-700 rounded-xl font-semibold text-sm hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50 transition-all"
              style={{ fontFamily: "Montserrat, sans-serif" }}
            >
              Sign In
            </motion.button>
          </div>

          <p className="text-gray-400 text-xs mt-8">
            Already registered? Sign in and we'll take you exactly where you left off.
          </p>
        </motion.div>
      </div>
    </div>
  );
}

// ─── Main Onboarding Component ───────────────────────────────────────────────

export default function Onboarding() {
  const { user, updateOnboardingStep, updateAuthProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const saved = useRef(loadSavedState());
  // Track last synced step to avoid calling updateOnboardingStep on every user object recreation
  const lastSyncedStep = useRef(null);

  // Detect "Add Outlet" mode: clear stale session and jump straight to step 3 (Business Details)
  const isAddOutletMode = new URLSearchParams(location.search).get('addOutlet') === 'true';

  const [currentStep, setCurrentStep] = useState(saved.current?.currentStep || 0);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [globalError, setGlobalError] = useState("");

  // Step 1: Account Registration
  const [fullName, setFullName] = useState(saved.current?.fullName || "");
  const [email, setEmail] = useState(saved.current?.email || "");
  const [phone, setPhone] = useState(saved.current?.phone || "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Step 2: Verification
  const [emailOtp, setEmailOtp] = useState("");
  const [whatsAppOtp, setWhatsAppOtp] = useState("");
  const [emailVerified, setEmailVerified] = useState(saved.current?.emailVerified || false);
  const [whatsAppVerified, setWhatsAppVerified] = useState(saved.current?.whatsAppVerified || false);
  const [otpSent, setOtpSent] = useState(saved.current?.otpSent || false);
  const [whatsAppOtpSent, setWhatsAppOtpSent] = useState(saved.current?.whatsAppOtpSent || false);
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [editPhoneVal, setEditPhoneVal] = useState("");

  // Step 3: Owner & Business Details
  const [ownerName, setOwnerName] = useState(saved.current?.ownerName || "");
  const [contactPhone, setContactPhone] = useState(saved.current?.contactPhone || "");
  const [registeredAddress, setRegisteredAddress] = useState(saved.current?.registeredAddress || "");
  const [gstNumber, setGstNumber] = useState(saved.current?.gstNumber || "");
  const [panNumber, setPanNumber] = useState(saved.current?.panNumber || "");
  const [numberOfPartners, setNumberOfPartners] = useState(saved.current?.numberOfPartners || "1");

  // Step 4: Businesses
  const [hotels, setHotels] = useState(saved.current?.hotels || []);
  const [restaurants, setRestaurants] = useState(saved.current?.restaurants || []);
  const [shops, setShops] = useState(saved.current?.shops || []);
  const [expandedBusiness, setExpandedBusiness] = useState(null);
  const [shopCategories, setShopCategories] = useState([]);

  // Step 5: Plans
  const [planMode, setPlanMode] = useState(saved.current?.planMode || "bundles");
  const [selectedBundles, setSelectedBundles] = useState(saved.current?.selectedBundles || []);
  const [selectedPlans, setSelectedPlans] = useState(saved.current?.selectedPlans || []);
  const [selectedDuration, setSelectedDuration] = useState(saved.current?.selectedDuration || null);
  const [couponCode, setCouponCode] = useState(saved.current?.couponCode || "");
  const [couponApplied, setCouponApplied] = useState(saved.current?.couponApplied || false);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [durations, setDurations] = useState([]);
  const [pricingPlans, setPricingPlans] = useState([]);

  // Step 7: Payment
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentId, setPaymentId] = useState("");
  const [paymentError, setPaymentError] = useState("");

  // Step 8: Success countdown
  const [countdown, setCountdown] = useState(5);

  // ─── Persist form data to sessionStorage on changes ────────────────────────
  useEffect(() => {
    // Don't save after payment success (clear instead)
    if (paymentSuccess) { clearSavedState(); return; }
    saveState({
      currentStep, fullName, email, phone,
      emailVerified, whatsAppVerified, otpSent, whatsAppOtpSent,
      ownerName, contactPhone, registeredAddress, gstNumber, panNumber, numberOfPartners,
      hotels, restaurants, shops,
      planMode, selectedBundles, selectedPlans, selectedDuration, couponCode, couponApplied,
    });
  }, [currentStep, fullName, email, phone, emailVerified, whatsAppVerified, otpSent, whatsAppOtpSent, ownerName, contactPhone, registeredAddress, gstNumber, panNumber, numberOfPartners, hotels, restaurants, shops, planMode, selectedBundles, selectedPlans, selectedDuration, couponCode, couponApplied, paymentSuccess]);

  // ─── Auth-aware initialization ────────────────────────────────────────────────
  // When a logged-in user opens /onboarding:
  //  1. Pre-fill their email / name / phone from the auth context
  //  2. Fetch backend progress to know the minimum step
  //  3. Resume from max(sessionStorage step, backend step) and never show step 0 again
  useEffect(() => {
    if (!user) return; // gate screen handles non-logged-in case

    // ── Add Outlet mode: clear stale draft and land on Business step ──
    if (isAddOutletMode) {
      clearSavedState();
      saved.current = null;
      // Pre-fill profile fields from auth
      if (user.email) setEmail(user.email);
      if (user.ownerName) { setFullName(user.ownerName); setOwnerName(user.ownerName); }
      if (user.contactPhone) { setPhone(user.contactPhone); setContactPhone(user.contactPhone); }
      if (user.isVerified) setEmailVerified(true);
      if (user.phoneVerified) setWhatsAppVerified(true);
      setCurrentStep(3); // Jump to Business Details step
      return;
    }

    // Pre-fill from auth profile if fields are empty
    if (!email && user.email) setEmail(user.email);
    if (!fullName && user.ownerName) setFullName(user.ownerName);
    if (!ownerName && user.ownerName) setOwnerName(user.ownerName);
    if (!phone && user.contactPhone) setPhone(user.contactPhone);
    if (!contactPhone && user.contactPhone) setContactPhone(user.contactPhone);

    // Sync verification status from auth profile
    if (user.isVerified && !emailVerified) setEmailVerified(true);
    if (user.phoneVerified && !whatsAppVerified) setWhatsAppVerified(true);

    // Fetch backend progress and resume
    getOwnerOnboardingProgress(user.email)
      .then((data) => {
        const backendStep = data?.currentStep ?? 1;
        const localStep = saved.current?.currentStep ?? 0;
        // Cap at step 6 (Review) max — never auto-jump to Payment (6) or Done (7)
        // so existing owners don't land on Payment Failed screen
        const cappedBackend = Math.min(backendStep, 5);
        // Logged-in users always start from at least step 1 (skip Account step 0)
        const resolvedStep = Math.max(cappedBackend, localStep, 1);
        setCurrentStep(resolvedStep);

        // Also pre-fill from backend profile if available
        if (data?.ownerName && !ownerName) setOwnerName(data.ownerName);
        if (data?.contactPhone && !contactPhone) setContactPhone(data.contactPhone);
        if (data?.contactPhone && !phone) setPhone(data.contactPhone);
        if (data?.isVerified) setEmailVerified(true);
        if (data?.phoneVerified) setWhatsAppVerified(true);
      })
      .catch(() => {
        // On failure, ensure logged-in user is at least on step 1
        if (currentStep === 0) setCurrentStep(1);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email, isAddOutletMode]); // Re-run when user logs in/out or mode changes

  // Sync step changes back to AuthContext
  // IMPORTANT: Only depend on currentStep - user object reference changes on every AuthContext
  // state update which would cause an infinite loop (updateOnboardingStep -> setAuthData -> new user obj -> re-render).
  useEffect(() => {
    if (user && updateOnboardingStep && lastSyncedStep.current !== currentStep) {
      lastSyncedStep.current = currentStep;
      updateOnboardingStep(currentStep);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  // ─── Effects ─────────────────────────────────────────────────────────────────

  // Fetch shop categories when step 4 is reached (or on mount if already past step 4)
  useEffect(() => {
    if (currentStep >= 3 && shopCategories.length === 0) {
      const fetchCategories = () => {
        getShopCategories()
          .then((data) => { if (Array.isArray(data) && data.length > 0) setShopCategories(data); })
          .catch(() => {});
      };
      fetchCategories();
      // Retry after 3 seconds if first attempt fails
      const retryTimer = setTimeout(fetchCategories, 3000);
      return () => clearTimeout(retryTimer);
    }
  }, [currentStep, shopCategories.length]);

  // Fetch plans/durations when step 5 is reached (or on mount if already past step 5)
  useEffect(() => {
    if (currentStep >= 4 && pricingPlans.length === 0) {
      Promise.all([getSubscriptionPlans(), getSubscriptionDurations(), getPricingPlans()])
        .then(([plans, durs, pricing]) => {
          setSubscriptionPlans(Array.isArray(plans) ? plans : []);
          setDurations(Array.isArray(durs) ? durs : []);
          setPricingPlans(Array.isArray(pricing) ? pricing : []);
        })
        .catch(() => {}); // Silently fail - will show empty plans
    }
  }, [currentStep, pricingPlans.length]);

  // Countdown on success step
  useEffect(() => {
    if (currentStep === 7 && paymentSuccess && countdown > 0) {
      const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
      return () => clearTimeout(timer);
    }
    if (currentStep === 7 && paymentSuccess && countdown === 0) {
      window.location.href = "https://trivanamerchant.com/login";
    }
  }, [currentStep, paymentSuccess, countdown]);

  // Auto-advance Step 1 when both verifications are complete
  useEffect(() => {
    if (currentStep === 1 && emailVerified && whatsAppVerified) {
      if (updateAuthProfile) {
        updateAuthProfile({ isVerified: true, phoneVerified: true, onboardingStep: 2 });
      }
      const timer = setTimeout(() => {
        setCurrentStep(2);
      }, 1000); // 1s delay for user to see success state
      return () => clearTimeout(timer);
    }
  }, [currentStep, emailVerified, whatsAppVerified, updateAuthProfile]);

  // Pre-fill owner name from registration
  useEffect(() => {
    if (currentStep === 2 && !ownerName && fullName) {
      setOwnerName(fullName);
    }
  }, [currentStep, ownerName, fullName]);

  // ─── Validation ──────────────────────────────────────────────────────────────

  const validateStep = useCallback(
    (step) => {
      const errs = {};

      switch (step) {
        case 0: {
          if (!fullName.trim()) errs.fullName = "Full name is required";
          if (!email.trim()) errs.email = "Email is required";
          else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = "Invalid email format";
          if (!phone.trim()) errs.phone = "Phone number is required";
          else if (!/^\d{10}$/.test(phone)) errs.phone = "Phone must be 10 digits";
          if (!password) errs.password = "Password is required";
          else if (password.length < 8) errs.password = "Password must be at least 8 characters";
          if (!confirmPassword) errs.confirmPassword = "Please confirm your password";
          else if (password !== confirmPassword) errs.confirmPassword = "Passwords do not match";
          break;
        }
        case 1: {
          if (!emailVerified) errs.emailOtp = "Email must be verified";
          if (!whatsAppVerified) errs.whatsAppOtp = "WhatsApp must be verified";
          break;
        }
        case 2: {
          if (!ownerName.trim()) errs.ownerName = "Owner name is required";
          if (!contactPhone.trim()) errs.contactPhone = "Contact phone is required";
          else if (!/^\d{10}$/.test(contactPhone)) errs.contactPhone = "Phone must be 10 digits";
          if (!registeredAddress.trim()) errs.registeredAddress = "Registered address is required";
          if (gstNumber && !/^[A-Z0-9]{15}$/.test(gstNumber.toUpperCase()))
            errs.gstNumber = "GST must be 15 uppercase alphanumeric characters";
          if (panNumber && !/^[A-Z0-9]{10}$/.test(panNumber.toUpperCase()))
            errs.panNumber = "PAN must be 10 uppercase alphanumeric characters";
          break;
        }
        case 3: {
          const totalBusinesses = hotels.length + restaurants.length + shops.length;
          if (totalBusinesses === 0) errs.business = "Add at least one business";
          hotels.forEach((h, i) => {
            if (!h.hotelName) errs[`hotel_${i}_name`] = "Hotel name is required";
            if (!h.address) errs[`hotel_${i}_address`] = "Address is required";
            if (!h.city) errs[`hotel_${i}_city`] = "City is required";
            if (!h.state) errs[`hotel_${i}_state`] = "State is required";
          });
          restaurants.forEach((r, i) => {
            if (!r.name) errs[`rest_${i}_name`] = "Restaurant name is required";
            if (!r.cuisineType) errs[`rest_${i}_cuisine`] = "Cuisine type is required";
            if (!r.address) errs[`rest_${i}_address`] = "Address is required";
            if (!r.city) errs[`rest_${i}_city`] = "City is required";
            if (!r.state) errs[`rest_${i}_state`] = "State is required";
          });
          shops.forEach((s, i) => {
            if (!s.shopName) errs[`shop_${i}_name`] = "Shop name is required";
            if (!s.category) errs[`shop_${i}_category`] = "Category is required";
            if (!s.contactPhone) errs[`shop_${i}_phone`] = "Contact phone is required";
            if (!s.addressLine1) errs[`shop_${i}_address`] = "Address is required";
            if (!s.city) errs[`shop_${i}_city`] = "City is required";
            if (!s.state) errs[`shop_${i}_state`] = "State is required";
            if (!s.pincode) errs[`shop_${i}_pincode`] = "Pincode is required";
          });
          break;
        }
        case 4: {
          if (selectedBundles.length === 0)
            errs.plans = "Select at least one bundle";
          break;
        }
        default:
          break;
      }

      setErrors(errs);
      return Object.keys(errs).length === 0;
    },
    [fullName, email, phone, password, confirmPassword, emailVerified, whatsAppVerified, ownerName, contactPhone, registeredAddress, gstNumber, panNumber, hotels, restaurants, shops, planMode, selectedBundles, selectedPlans, selectedDuration]
  );

  // ─── Step Handlers ───────────────────────────────────────────────────────────

  const handleNext = async () => {
    setGlobalError("");
    if (!validateStep(currentStep)) return;

    // Step 0: Register owner
    if (currentStep === 0) {
      setLoading(true);
      try {
        await registerOwner({
          ownerName: fullName.trim(),
          contactEmail: email.trim(),
          contactPhone: phone.trim(),
          password,
        });
        setOtpSent(true);
        setCurrentStep(1);
      } catch (err) {
        setGlobalError(err.message || "Registration failed. Please try again.");
      } finally {
        setLoading(false);
      }
      return;
    }

    // Step 1: Verify (both must be verified)
    if (currentStep === 1) {
      if (emailVerified && whatsAppVerified) {
        // Mark verification in AuthContext so header and other pages know
        if (updateAuthProfile) {
          updateAuthProfile({ isVerified: true, phoneVerified: true, onboardingStep: 2 });
        }
        setCurrentStep(2);
      }
      return;
    }

    // Steps 2-4: just advance
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
      return;
    }

    // Step 5: advance to review
    if (currentStep === 5) {
      setCurrentStep(6);
      return;
    }
  };

  const handleBack = () => {
    // Logged-in users can never go back to step 0 (Account creation)
    const minStep = user ? 1 : 0;
    if (currentStep > minStep) {
      setErrors({});
      setGlobalError("");
      setCurrentStep(currentStep - 1);
    }
  };

  // ─── OTP Handlers ────────────────────────────────────────────────────────────

  const handleVerifyEmail = async () => {
    if (!emailOtp || emailOtp.length !== 6) {
      setErrors((e) => ({ ...e, emailOtp: "Enter a valid 6-digit OTP" }));
      return;
    }
    setLoading(true);
    try {
      await verifyOwnerEmail({ email: email.trim(), otp: emailOtp });
      setEmailVerified(true);
      setErrors((e) => { const { emailOtp: _, ...rest } = e; return rest; });
    } catch (err) {
      setErrors((e) => ({ ...e, emailOtp: err.message || "Invalid OTP" }));
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmailOtp = async () => {
    setLoading(true);
    try {
      await resendOwnerOtp({ email: email.trim() });
      setOtpSent(true);
    } catch (err) {
      setGlobalError(err.message || "Failed to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleSendWhatsAppOtp = async () => {
    setLoading(true);
    try {
      await sendWhatsAppOtp({ email: email.trim(), phone: phone.trim() });
      setWhatsAppOtpSent(true);
    } catch (err) {
      setGlobalError(err.message || "Failed to send WhatsApp OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyWhatsApp = async () => {
    if (!whatsAppOtp || whatsAppOtp.length !== 6) {
      setErrors((e) => ({ ...e, whatsAppOtp: "Enter a valid 6-digit OTP" }));
      return;
    }
    setLoading(true);
    try {
      await verifyWhatsAppOtp({ email: email.trim(), otp: whatsAppOtp });
      setWhatsAppVerified(true);
      setErrors((e) => { const { whatsAppOtp: _, ...rest } = e; return rest; });
    } catch (err) {
      setErrors((e) => ({ ...e, whatsAppOtp: err.message || "Invalid OTP" }));
    } finally {
      setLoading(false);
    }
  };

  // ─── Business Handlers ───────────────────────────────────────────────────────

  const addHotel = () => {
    setHotels([...hotels, { hotelName: "", address: "", city: "", state: "", country: "India", starRating: "", peopleCapacity: "", staffCount: "", dateOfEstablishment: "", features: [] }]);
    setExpandedBusiness(`hotel-${hotels.length}`);
  };

  const addRestaurant = () => {
    setRestaurants([...restaurants, { name: "", cuisineType: "", seatingCapacity: "", address: "", city: "", state: "", country: "India", hasBar: false, petAllowed: false }]);
    setExpandedBusiness(`restaurant-${restaurants.length}`);
  };

  const addShop = () => {
    setShops([...shops, { shopName: "", category: "", contactPhone: "", contactEmail: "", whatsappNumber: "", addressLine1: "", addressLine2: "", city: "", state: "", country: "India", pincode: "", gstNumber: "", fssaiLicense: "", panNumber: "", description: "" }]);
    setExpandedBusiness(`shop-${shops.length}`);
  };

  const updateHotel = (index, field, value) => {
    const updated = [...hotels];
    updated[index] = { ...updated[index], [field]: value };
    setHotels(updated);
  };

  const updateRestaurant = (index, field, value) => {
    const updated = [...restaurants];
    updated[index] = { ...updated[index], [field]: value };
    setRestaurants(updated);
  };

  const updateShop = (index, field, value) => {
    const updated = [...shops];
    updated[index] = { ...updated[index], [field]: value };
    setShops(updated);
  };

  const removeHotel = (index) => setHotels(hotels.filter((_, i) => i !== index));
  const removeRestaurant = (index) => setRestaurants(restaurants.filter((_, i) => i !== index));
  const removeShop = (index) => setShops(shops.filter((_, i) => i !== index));

  const toggleHotelFeature = (hotelIndex, feature) => {
    const updated = [...hotels];
    const features = updated[hotelIndex].features || [];
    if (features.includes(feature)) {
      updated[hotelIndex].features = features.filter((f) => f !== feature);
    } else {
      updated[hotelIndex].features = [...features, feature];
    }
    setHotels(updated);
  };

  // ─── Plan Handlers ───────────────────────────────────────────────────────────

  const toggleBundle = (bundleId) => {
    setSelectedBundles((prev) =>
      prev.includes(bundleId) ? prev.filter((b) => b !== bundleId) : [...prev, bundleId]
    );
  };

  const togglePlan = (planId) => {
    setSelectedPlans((prev) =>
      prev.includes(planId) ? prev.filter((p) => p !== planId) : [...prev, planId]
    );
  };

  const applyCoupon = () => {
    if (couponCode && COUPONS[couponCode.toUpperCase()]) {
      setCouponApplied(true);
      setErrors((e) => { const { coupon: _, ...rest } = e; return rest; });
    } else {
      setErrors((e) => ({ ...e, coupon: "Invalid coupon code" }));
      setCouponApplied(false);
    }
  };

  // ─── Pricing Calculation ─────────────────────────────────────────────────────

  const pricing = useMemo(() => {
    return calculateTotal(selectedPlans, selectedBundles, pricingPlans, selectedDuration, couponApplied ? couponCode : "");
  }, [selectedPlans, selectedBundles, pricingPlans, selectedDuration, couponCode, couponApplied]);

  // Build enriched bundles from live DB plan data (same logic as Pricing.js)
  const enrichedBundles = useMemo(() => {
    if (!pricingPlans.length) return [];
    const plansById = new Map(pricingPlans.map((p) => [p.planId ?? p.id, p]));
    return BUNDLE_DEFINITIONS.map((def) => {
      const bundlePlans = def.planIds.map((id) => plansById.get(id)).filter(Boolean);
      const monthlyTotal = bundlePlans.reduce((sum, p) => sum + parsePrice(p.basePrice), 0);
      const yearlyTotal = monthlyTotal * MONTHS_IN_YEAR;
      const bundlePrice = (def.percentage / 100) * yearlyTotal;
      return { ...def, bundlePlans, monthlyTotal, yearlyTotal, bundlePrice };
    }).filter((b) => b.bundlePlans.length > 0);
  }, [pricingPlans]);

  // ─── Payment Handler ─────────────────────────────────────────────────────────

  const handlePayment = async () => {
    setLoading(true);
    setPaymentError("");
    try {
      const amountInPaise = Math.round(pricing.grandTotal * 100);
      const order = await createRazorpayOrder({ amount: amountInPaise });

      const options = {
        key: RAZORPAY_KEY_ID,
        amount: amountInPaise,
        currency: "INR",
        name: "Trivana",
        description: "Business Onboarding Subscription",
        order_id: order.id || order.orderId,
        handler: async (response) => {
          try {
            await verifyRazorpayPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            await submitOnboarding(response.razorpay_payment_id);
            setPaymentId(response.razorpay_payment_id);
            setPaymentSuccess(true);
            setCurrentStep(7);
          } catch (err) {
            setPaymentError(err.message || "Payment verification failed");
            setCurrentStep(7);
          }
        },
        prefill: {
          name: ownerName || fullName,
          email: email,
          contact: `+91${contactPhone || phone}`,
        },
        theme: { color: "#2563EB" },
        modal: {
          ondismiss: () => {
            setLoading(false);
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response) => {
        setPaymentError(response.error?.description || "Payment failed");
        setCurrentStep(7);
      });
      rzp.open();
    } catch (err) {
      setPaymentError(err.message || "Failed to create payment order");
    } finally {
      setLoading(false);
    }
  };

  // ─── Submit Onboarding ───────────────────────────────────────────────────────

  const submitOnboarding = async (transactionId) => {
    const businesses = [];

    hotels.forEach((h) => {
      businesses.push({
        businessType: "HOTEL",
        hotel: {
          hotelName: h.hotelName,
          address: h.address,
          city: h.city,
          state: h.state,
          country: h.country || "India",
          starRating: h.starRating ? parseInt(h.starRating) : null,
          peopleCapacity: h.peopleCapacity ? parseInt(h.peopleCapacity) : null,
          staffCount: h.staffCount ? parseInt(h.staffCount) : null,
          dateOfEstablishment: h.dateOfEstablishment || null,
          features: h.features || [],
        },
      });
    });

    restaurants.forEach((r) => {
      businesses.push({
        businessType: "RESTAURANT",
        restaurant: {
          name: r.name,
          cuisineType: r.cuisineType,
          seatingCapacity: r.seatingCapacity ? parseInt(r.seatingCapacity) : null,
          address: r.address,
          city: r.city,
          state: r.state,
          country: r.country || "India",
          hasBar: r.hasBar || false,
          petAllowed: r.petAllowed || false,
        },
      });
    });

    shops.forEach((s) => {
      businesses.push({
        businessType: "SHOP",
        shop: {
          shopName: s.shopName,
          category: s.category,
          contactPhone: s.contactPhone,
          contactEmail: s.contactEmail || null,
          whatsappNumber: s.whatsappNumber || null,
          address: s.addressLine1,
          addressLine2: s.addressLine2 || null,
          city: s.city,
          state: s.state,
          country: s.country || "India",
          pincode: s.pincode,
          gstNumber: s.gstNumber || null,
          fssaiLicense: s.fssaiLicense || null,
          panNumber: s.panNumber || null,
          description: s.description || null,
        },
      });
    });

    const payload = {
      owner: {
        ownerName: ownerName.trim(),
        email: email.trim(),
        contactPhone: (contactPhone || phone).trim(),
        gstNumber: gstNumber ? gstNumber.toUpperCase().trim() : null,
        panNumber: panNumber ? panNumber.toUpperCase().trim() : null,
        registeredAddress: registeredAddress.trim(),
        numberOfPartners: parseInt(numberOfPartners) || 1,
        emailVerified: true,
        status: "PENDING",
      },
      businesses,
      subscription: {
        subscriptionPlanId: selectedPlans[0] || (selectedBundles.length > 0 ? BUNDLE_DEFINITIONS.find((b) => b.id === selectedBundles[0])?.planIds[0] : null),
        planDurationId: selectedDuration?.id || selectedDuration?.durationId || null,
        transactionId,
        paymentMode: "RAZORPAY",
        amount: pricing.grandTotal,
      },
    };

    await createMultipleBusinessOnboarding(payload);
  };

  // ─── Step Renderers ──────────────────────────────────────────────────────────

  const renderStep1 = () => (
    <FormCard>
      <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-1 sm:mb-2">Create Your Account</h2>
      <p className="text-gray-500 text-xs sm:text-sm mb-5 sm:mb-6">Get started with Trivana in minutes</p>

      <InputField
        label="Full Name"
        value={fullName}
        onChange={(e) => setFullName(e.target.value)}
        placeholder="Enter your full name"
        required
        error={errors.fullName}
      />

      <InputField
        label="Email Address"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        required
        error={errors.email}
      />

      <div className="mb-4 sm:mb-5">
        <label className="block text-[12px] sm:text-[13px] font-medium text-gray-500 mb-1 sm:mb-1.5 uppercase tracking-wide">
          Phone Number <span className="text-red-400">*</span>
        </label>
        <div className="flex items-center border-b-2 border-gray-200 focus-within:border-blue-500 transition-colors">
          <span className="text-gray-400 text-sm pr-2">+91</span>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
            placeholder="9876543210"
            className="flex-1 px-0 py-2 sm:py-2.5 bg-transparent border-0 text-sm sm:text-base focus:outline-none focus:ring-0 text-gray-800 placeholder:text-gray-300"
          />
        </div>
        {errors.phone && <p className="text-red-500 text-[11px] sm:text-xs mt-1">{errors.phone}</p>}
      </div>

      <div className="mb-4 sm:mb-5">
        <label className="block text-[12px] sm:text-[13px] font-medium text-gray-500 mb-1 sm:mb-1.5 uppercase tracking-wide">
          Password <span className="text-red-400">*</span>
        </label>
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Min 8 characters"
            className={`w-full px-0 py-2 sm:py-2.5 pr-10 bg-transparent border-0 border-b-2 text-sm sm:text-base ${
              errors.password ? "border-red-300" : "border-gray-200 focus:border-blue-500"
            } focus:outline-none focus:ring-0 transition-colors text-gray-800 placeholder:text-gray-300`}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
          >
            {showPassword ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
          </button>
        </div>
        {errors.password && <p className="text-red-500 text-[11px] sm:text-xs mt-1">{errors.password}</p>}
        {password && (
          <div className="mt-2 flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full ${strengthLabel(getPasswordStrength(password)).color} transition-all duration-300 rounded-full`}
                style={{ width: `${(getPasswordStrength(password) / 5) * 100}%` }}
              />
            </div>
            <span className="text-[11px] text-gray-500">{strengthLabel(getPasswordStrength(password)).text}</span>
          </div>
        )}
      </div>

      <div className="mb-4 sm:mb-5">
        <label className="block text-[12px] sm:text-[13px] font-medium text-gray-500 mb-1 sm:mb-1.5 uppercase tracking-wide">
          Confirm Password <span className="text-red-400">*</span>
        </label>
        <div className="relative">
          <input
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Re-enter your password"
            className={`w-full px-0 py-2 sm:py-2.5 pr-10 bg-transparent border-0 border-b-2 text-sm sm:text-base ${
              errors.confirmPassword ? "border-red-300" : "border-gray-200 focus:border-blue-500"
            } focus:outline-none focus:ring-0 transition-colors text-gray-800 placeholder:text-gray-300`}
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
          >
            {showConfirmPassword ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
          </button>
        </div>
        {errors.confirmPassword && <p className="text-red-500 text-[11px] sm:text-xs mt-1">{errors.confirmPassword}</p>}
      </div>
    </FormCard>
  );

  const renderStep2 = () => (
    <FormCard>
      <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-1 sm:mb-2">Verify Your Identity</h2>
      <p className="text-gray-500 text-xs sm:text-sm mb-4 sm:mb-6">We sent verification codes to your email and WhatsApp</p>

      {/* Email OTP */}
      <div className="mb-6 p-4 rounded-xl bg-blue-50/50 border border-blue-100">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-700">Email Verification</h3>
          {emailVerified && (
            <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
              <FaCheck className="w-3 h-3" /> Verified
            </span>
          )}
        </div>
        {!emailVerified ? (
          <>
            <div className="flex gap-2">
              <input
                type="text"
                value={emailOtp}
                onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="Enter 6-digit OTP"
                maxLength={6}
                className={`flex-1 px-4 py-2.5 rounded-lg border ${
                  errors.emailOtp ? "border-red-400" : "border-gray-300"
                } focus:outline-none focus:ring-2 focus:ring-blue-300`}
              />
              <motion.button
                whileHover={buttonHover}
                whileTap={buttonTap}
                onClick={handleVerifyEmail}
                disabled={loading}
                className="px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                Verify
              </motion.button>
            </div>
            {errors.emailOtp && <p className="text-red-500 text-xs mt-1">{errors.emailOtp}</p>}
            <button
              onClick={handleResendEmailOtp}
              disabled={loading}
              className="text-blue-600 text-sm mt-2 hover:underline disabled:opacity-50"
            >
              Resend OTP
            </button>
          </>
        ) : (
          <p className="text-green-600 text-sm">Your email has been verified successfully.</p>
        )}
      </div>

      {/* WhatsApp OTP */}
      <div className="p-4 rounded-xl bg-green-50/50 border border-green-100">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-700">WhatsApp Verification</h3>
          {whatsAppVerified && (
            <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
              <FaCheck className="w-3 h-3" /> Verified
            </span>
          )}
        </div>
        {!whatsAppVerified ? (
          <>
            {/* Phone display & editing container */}
            <div className="mb-4">
              {isEditingPhone ? (
                <div className="flex flex-col gap-1.5">
                  <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                    Edit WhatsApp Number
                  </label>
                  <div className="flex gap-2 items-center">
                    <div className="flex-1 flex items-center border border-gray-300 rounded-lg px-3 py-2 bg-white focus-within:ring-2 focus-within:ring-green-300 focus-within:border-green-400">
                      <span className="text-gray-400 text-sm pr-1.5 font-medium">+91</span>
                      <input
                        type="tel"
                        value={editPhoneVal}
                        onChange={(e) => setEditPhoneVal(e.target.value.replace(/\D/g, "").slice(0, 10))}
                        placeholder="9876543210"
                        className="flex-1 bg-transparent border-0 p-0 text-sm focus:outline-none focus:ring-0 text-gray-800"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        if (!/^\d{10}$/.test(editPhoneVal)) {
                          setErrors((e) => ({ ...e, editPhone: "Phone must be 10 digits" }));
                          return;
                        }
                        setPhone(editPhoneVal);
                        setContactPhone(editPhoneVal);
                        setWhatsAppOtpSent(false); // Reset sent state so they have to request OTP for new number
                        setIsEditingPhone(false);
                        setErrors((e) => { const { editPhone: _, ...rest } = e; return rest; });
                      }}
                      className="p-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center justify-center"
                      title="Save"
                    >
                      <FaCheck className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditingPhone(false);
                        setErrors((e) => { const { editPhone: _, ...rest } = e; return rest; });
                      }}
                      className="px-3 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 text-sm font-semibold transition"
                    >
                      Cancel
                    </button>
                  </div>
                  {errors.editPhone && <p className="text-red-500 text-xs mt-0.5">{errors.editPhone}</p>}
                </div>
              ) : (
                <div className="flex items-center justify-between bg-white/60 rounded-lg p-2.5 border border-gray-200/50">
                  <div className="flex flex-col">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">
                      WhatsApp Number
                    </span>
                    <span className="text-sm font-bold text-gray-700 mt-0.5">
                      +91 {phone || "Not set"}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setEditPhoneVal(phone || "");
                      setIsEditingPhone(true);
                    }}
                    className="flex items-center gap-1 px-2.5 py-1 border border-gray-200 text-gray-600 rounded-lg hover:border-green-300 hover:text-green-600 hover:bg-green-50 text-xs font-semibold transition"
                  >
                    <FaEdit className="w-3 h-3" /> Change
                  </button>
                </div>
              )}
            </div>

            {!whatsAppOtpSent ? (
              <motion.button
                whileHover={buttonHover}
                whileTap={buttonTap}
                onClick={handleSendWhatsAppOtp}
                disabled={loading || isEditingPhone}
                className="px-4 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50"
              >
                Send WhatsApp OTP
              </motion.button>
            ) : (
              <>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={whatsAppOtp}
                    onChange={(e) => setWhatsAppOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="Enter 6-digit OTP"
                    maxLength={6}
                    className={`flex-1 px-4 py-2.5 rounded-lg border ${
                      errors.whatsAppOtp ? "border-red-400" : "border-gray-300"
                    } focus:outline-none focus:ring-2 focus:ring-green-300`}
                  />
                  <motion.button
                    whileHover={buttonHover}
                    whileTap={buttonTap}
                    onClick={handleVerifyWhatsApp}
                    disabled={loading}
                    className="px-4 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50"
                  >
                    Verify
                  </motion.button>
                </div>
                {errors.whatsAppOtp && <p className="text-red-500 text-xs mt-1">{errors.whatsAppOtp}</p>}
                <button
                  onClick={handleSendWhatsAppOtp}
                  disabled={loading}
                  className="text-green-600 text-sm mt-2 hover:underline disabled:opacity-50"
                >
                  Resend OTP
                </button>
              </>
            )}
          </>
        ) : (
          <div className="flex flex-col gap-0.5">
            <p className="text-green-600 text-sm font-semibold">Your WhatsApp has been verified successfully.</p>
            <span className="text-xs text-gray-500">Verified number: +91 {phone}</span>
          </div>
        )}
      </div>
    </FormCard>
  );

  const renderStep3 = () => (
    <FormCard>
      <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-1 sm:mb-2">Owner & Business Details</h2>
      <p className="text-gray-500 text-xs sm:text-sm mb-4 sm:mb-6">Tell us about yourself and your business registration</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
        <InputField
          label="Owner Name"
          value={ownerName}
          onChange={(e) => setOwnerName(e.target.value)}
          placeholder="Full name"
          required
          error={errors.ownerName}
        />
        <InputField
          label="Contact Phone"
          value={contactPhone}
          onChange={(e) => setContactPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
          placeholder="10-digit phone"
          required
          error={errors.contactPhone}
        />
      </div>

      <InputField
        label="Registered Address"
        value={registeredAddress}
        onChange={(e) => setRegisteredAddress(e.target.value)}
        placeholder="Complete registered address"
        required
        error={errors.registeredAddress}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
        <InputField
          label="GST Number"
          value={gstNumber}
          onChange={(e) => setGstNumber(e.target.value.toUpperCase().slice(0, 15))}
          placeholder="15-character GST (optional)"
          maxLength={15}
          error={errors.gstNumber}
        />
        <InputField
          label="PAN Number"
          value={panNumber}
          onChange={(e) => setPanNumber(e.target.value.toUpperCase().slice(0, 10))}
          placeholder="10-character PAN (optional)"
          maxLength={10}
          error={errors.panNumber}
        />
      </div>

      <InputField
        label="Number of Partners"
        type="number"
        value={numberOfPartners}
        onChange={(e) => setNumberOfPartners(e.target.value)}
        placeholder="1"
      />
    </FormCard>
  );

  const renderStep4 = () => (
    <FormCard>
      <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-1 sm:mb-2">Add Your Businesses</h2>
      <p className="text-gray-500 text-xs sm:text-sm mb-4 sm:mb-6">Add at least one business to continue</p>

      {errors.business && <p className="text-red-500 text-sm mb-4">{errors.business}</p>}

      {/* Add Business Buttons */}
      <div className="flex flex-wrap gap-3 mb-6">
        <motion.button
          whileHover={buttonHover}
          whileTap={buttonTap}
          onClick={addHotel}
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-50 text-blue-700 rounded-lg border border-blue-200 hover:bg-blue-100 font-medium text-sm"
        >
          <FaHotel /> <FaPlus className="w-3 h-3" /> Hotel
        </motion.button>
        <motion.button
          whileHover={buttonHover}
          whileTap={buttonTap}
          onClick={addRestaurant}
          className="flex items-center gap-2 px-4 py-2.5 bg-orange-50 text-orange-700 rounded-lg border border-orange-200 hover:bg-orange-100 font-medium text-sm"
        >
          <FaUtensils /> <FaPlus className="w-3 h-3" /> Restaurant
        </motion.button>
        <motion.button
          whileHover={buttonHover}
          whileTap={buttonTap}
          onClick={addShop}
          className="flex items-center gap-2 px-4 py-2.5 bg-purple-50 text-purple-700 rounded-lg border border-purple-200 hover:bg-purple-100 font-medium text-sm"
        >
          <FaStore /> <FaPlus className="w-3 h-3" /> Shop
        </motion.button>
      </div>

      {/* Hotels */}
      {hotels.map((hotel, index) => (
        <div key={`hotel-${index}`} className="mb-4 border border-gray-200 rounded-xl overflow-hidden">
          <div
            className="flex items-center justify-between p-4 bg-blue-50 cursor-pointer"
            onClick={() => setExpandedBusiness(expandedBusiness === `hotel-${index}` ? null : `hotel-${index}`)}
          >
            <div className="flex items-center gap-2">
              <FaHotel className="text-blue-600" />
              <span className="font-medium text-gray-700">{hotel.hotelName || `Hotel ${index + 1}`}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); removeHotel(index); }}
                className="text-red-400 hover:text-red-600 p-1"
              >
                <FaTrash className="w-3.5 h-3.5" />
              </button>
              {expandedBusiness === `hotel-${index}` ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
            </div>
          </div>
          <AnimatePresence>
            {expandedBusiness === `hotel-${index}` && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="p-4 space-y-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
                    <InputField label="Hotel Name" value={hotel.hotelName} onChange={(e) => updateHotel(index, "hotelName", e.target.value)} required error={errors[`hotel_${index}_name`]} placeholder="Hotel name" />
                    <SelectField label="Star Rating" value={hotel.starRating} onChange={(e) => updateHotel(index, "starRating", e.target.value)} options={[1,2,3,4,5].map(n => ({ value: String(n), label: `${n} Star` }))} placeholder="Select rating" />
                  </div>
                  <InputField label="Address" value={hotel.address} onChange={(e) => updateHotel(index, "address", e.target.value)} required error={errors[`hotel_${index}_address`]} placeholder="Full address" />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4">
                    <InputField label="City" value={hotel.city} onChange={(e) => updateHotel(index, "city", e.target.value)} required error={errors[`hotel_${index}_city`]} placeholder="City" />
                    <SelectField label="State" value={hotel.state} onChange={(e) => updateHotel(index, "state", e.target.value)} required error={errors[`hotel_${index}_state`]} options={INDIAN_STATES.map(s => ({ value: s, label: s }))} placeholder="Select state" />
                    <InputField label="Country" value={hotel.country} onChange={(e) => updateHotel(index, "country", e.target.value)} placeholder="India" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4">
                    <InputField label="People Capacity" type="number" value={hotel.peopleCapacity} onChange={(e) => updateHotel(index, "peopleCapacity", e.target.value)} placeholder="100" />
                    <InputField label="Staff Count" type="number" value={hotel.staffCount} onChange={(e) => updateHotel(index, "staffCount", e.target.value)} placeholder="20" />
                    <InputField label="Date of Establishment" type="date" value={hotel.dateOfEstablishment} onChange={(e) => updateHotel(index, "dateOfEstablishment", e.target.value)} />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Features</label>
                    <div className="flex flex-wrap gap-2">
                      {HOTEL_FEATURES.map((feature) => (
                        <label key={feature} className="flex items-center gap-1.5 text-sm bg-gray-50 px-3 py-1.5 rounded-lg border cursor-pointer hover:bg-blue-50">
                          <input type="checkbox" checked={hotel.features?.includes(feature)} onChange={() => toggleHotelFeature(index, feature)} className="rounded text-blue-600" />
                          {feature}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}

      {/* Restaurants */}
      {restaurants.map((rest, index) => (
        <div key={`restaurant-${index}`} className="mb-4 border border-gray-200 rounded-xl overflow-hidden">
          <div
            className="flex items-center justify-between p-4 bg-orange-50 cursor-pointer"
            onClick={() => setExpandedBusiness(expandedBusiness === `restaurant-${index}` ? null : `restaurant-${index}`)}
          >
            <div className="flex items-center gap-2">
              <FaUtensils className="text-orange-600" />
              <span className="font-medium text-gray-700">{rest.name || `Restaurant ${index + 1}`}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); removeRestaurant(index); }}
                className="text-red-400 hover:text-red-600 p-1"
              >
                <FaTrash className="w-3.5 h-3.5" />
              </button>
              {expandedBusiness === `restaurant-${index}` ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
            </div>
          </div>
          <AnimatePresence>
            {expandedBusiness === `restaurant-${index}` && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="p-4 space-y-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
                    <InputField label="Restaurant Name" value={rest.name} onChange={(e) => updateRestaurant(index, "name", e.target.value)} required error={errors[`rest_${index}_name`]} placeholder="Restaurant name" />
                    <InputField label="Cuisine Type" value={rest.cuisineType} onChange={(e) => updateRestaurant(index, "cuisineType", e.target.value)} required error={errors[`rest_${index}_cuisine`]} placeholder="e.g. Indian, Chinese" />
                  </div>
                  <InputField label="Seating Capacity" type="number" value={rest.seatingCapacity} onChange={(e) => updateRestaurant(index, "seatingCapacity", e.target.value)} placeholder="50" />
                  <InputField label="Address" value={rest.address} onChange={(e) => updateRestaurant(index, "address", e.target.value)} required error={errors[`rest_${index}_address`]} placeholder="Full address" />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4">
                    <InputField label="City" value={rest.city} onChange={(e) => updateRestaurant(index, "city", e.target.value)} required error={errors[`rest_${index}_city`]} placeholder="City" />
                    <SelectField label="State" value={rest.state} onChange={(e) => updateRestaurant(index, "state", e.target.value)} required error={errors[`rest_${index}_state`]} options={INDIAN_STATES.map(s => ({ value: s, label: s }))} placeholder="Select state" />
                    <InputField label="Country" value={rest.country} onChange={(e) => updateRestaurant(index, "country", e.target.value)} placeholder="India" />
                  </div>
                  <div className="flex gap-6 mt-2">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={rest.hasBar} onChange={(e) => updateRestaurant(index, "hasBar", e.target.checked)} className="rounded text-blue-600" />
                      Has Bar
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={rest.petAllowed} onChange={(e) => updateRestaurant(index, "petAllowed", e.target.checked)} className="rounded text-blue-600" />
                      Pet Allowed
                    </label>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}

      {/* Shops */}
      {shops.map((shop, index) => (
        <div key={`shop-${index}`} className="mb-4 border border-gray-200 rounded-xl overflow-hidden">
          <div
            className="flex items-center justify-between p-4 bg-purple-50 cursor-pointer"
            onClick={() => setExpandedBusiness(expandedBusiness === `shop-${index}` ? null : `shop-${index}`)}
          >
            <div className="flex items-center gap-2">
              <FaStore className="text-purple-600" />
              <span className="font-medium text-gray-700">{shop.shopName || `Shop ${index + 1}`}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); removeShop(index); }}
                className="text-red-400 hover:text-red-600 p-1"
              >
                <FaTrash className="w-3.5 h-3.5" />
              </button>
              {expandedBusiness === `shop-${index}` ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
            </div>
          </div>
          <AnimatePresence>
            {expandedBusiness === `shop-${index}` && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="p-4 space-y-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
                    <InputField label="Shop Name" value={shop.shopName} onChange={(e) => updateShop(index, "shopName", e.target.value)} required error={errors[`shop_${index}_name`]} placeholder="Shop name" />
                    <SelectField
                      label="Category"
                      value={shop.category}
                      onChange={(e) => updateShop(index, "category", e.target.value)}
                      required
                      error={errors[`shop_${index}_category`]}
                      options={shopCategories.map((c) => ({ value: c.categoryCode || c.categoryId, label: c.categoryName }))}
                      placeholder="Select category"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4">
                    <InputField label="Contact Phone" value={shop.contactPhone} onChange={(e) => updateShop(index, "contactPhone", e.target.value.replace(/\D/g, "").slice(0, 10))} required error={errors[`shop_${index}_phone`]} placeholder="Phone" />
                    <InputField label="Contact Email" value={shop.contactEmail} onChange={(e) => updateShop(index, "contactEmail", e.target.value)} placeholder="Email (optional)" />
                    <InputField label="WhatsApp Number" value={shop.whatsappNumber} onChange={(e) => updateShop(index, "whatsappNumber", e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="WhatsApp (optional)" />
                  </div>
                  <InputField label="Address Line 1" value={shop.addressLine1} onChange={(e) => updateShop(index, "addressLine1", e.target.value)} required error={errors[`shop_${index}_address`]} placeholder="Street address" />
                  <InputField label="Address Line 2" value={shop.addressLine2} onChange={(e) => updateShop(index, "addressLine2", e.target.value)} placeholder="Apt, suite, etc. (optional)" />
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-4">
                    <InputField label="City" value={shop.city} onChange={(e) => updateShop(index, "city", e.target.value)} required error={errors[`shop_${index}_city`]} placeholder="City" />
                    <SelectField label="State" value={shop.state} onChange={(e) => updateShop(index, "state", e.target.value)} required error={errors[`shop_${index}_state`]} options={INDIAN_STATES.map(s => ({ value: s, label: s }))} placeholder="Select state" />
                    <InputField label="Country" value={shop.country} onChange={(e) => updateShop(index, "country", e.target.value)} placeholder="India" />
                    <InputField label="Pincode" value={shop.pincode} onChange={(e) => updateShop(index, "pincode", e.target.value.replace(/\D/g, "").slice(0, 6))} required error={errors[`shop_${index}_pincode`]} placeholder="6-digit" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4">
                    <InputField label="GST Number" value={shop.gstNumber} onChange={(e) => updateShop(index, "gstNumber", e.target.value.toUpperCase())} placeholder="Optional" />
                    <InputField label="FSSAI License" value={shop.fssaiLicense} onChange={(e) => updateShop(index, "fssaiLicense", e.target.value)} placeholder="Optional" />
                    <InputField label="PAN Number" value={shop.panNumber} onChange={(e) => updateShop(index, "panNumber", e.target.value.toUpperCase())} placeholder="Optional" />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={shop.description}
                      onChange={(e) => updateShop(index, "description", e.target.value)}
                      placeholder="Brief description of your shop (optional)"
                      rows={3}
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none"
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </FormCard>
  );

  const renderStep5 = () => {
    const plansLoading = pricingPlans.length === 0;

    return (
    <FormCard>
      <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-1 sm:mb-2">Select Your Plan</h2>
      <p className="text-gray-500 text-xs sm:text-sm mb-4 sm:mb-6">
        Choose the bundle that fits your business — prices are sourced directly from our plans database.
      </p>

      {errors.plans && <p className="text-red-500 text-sm mb-4">{errors.plans}</p>}

      {/* Loading state while plans fetch */}
      {plansLoading ? (
        <div className="flex flex-col items-center justify-center py-10 gap-3 text-gray-400">
          <svg className="animate-spin h-8 w-8 text-blue-500" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-sm">Loading plans from database…</span>
        </div>
      ) : (
        <div className="space-y-4">
          {enrichedBundles.map((bundle) => {
            const isSelected = selectedBundles.includes(bundle.id);
            return (
              <motion.div
                key={bundle.id}
                whileHover={{ scale: 1.01 }}
                onClick={() => toggleBundle(bundle.id)}
                className={`rounded-xl border-2 cursor-pointer transition-all overflow-hidden ${
                  isSelected
                    ? "border-blue-500 shadow-md shadow-blue-100"
                    : "border-gray-200 hover:border-blue-300 hover:shadow-sm"
                }`}
              >
                {/* Bundle header */}
                <div className={`p-4 ${isSelected ? "bg-blue-50" : "bg-gray-50"}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-bold text-gray-800 text-sm sm:text-base">{bundle.name}</h4>
                        {isSelected && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-600 text-white text-xs font-semibold rounded-full">
                            <FaCheck className="w-2.5 h-2.5" /> Selected
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{bundle.description}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-extrabold text-xl text-blue-600">{formatCurrency(bundle.bundlePrice)}</p>
                      <p className="text-xs text-gray-400">per year</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {bundle.percentage}% of ₹{(bundle.yearlyTotal).toLocaleString("en-IN")}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Plans included in this bundle */}
                <div className="px-4 py-3 bg-white border-t border-gray-100">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                    Includes {bundle.bundlePlans.length} plan{bundle.bundlePlans.length > 1 ? "s" : ""}
                  </p>
                  <ul className="space-y-1.5">
                    {bundle.bundlePlans.map((plan) => (
                      <li key={plan.planId ?? plan.id}
                        className="flex items-center justify-between text-xs bg-blue-50/60 px-3 py-1.5 rounded-lg border border-blue-100"
                      >
                        <span className="font-medium text-gray-700 truncate pr-2">{plan.name ?? plan.planName}</span>
                        <span className="font-bold text-blue-600 whitespace-nowrap">
                          {formatCurrency(parsePrice(plan.basePrice) * MONTHS_IN_YEAR)} / yr
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            );
          })}

          {enrichedBundles.length === 0 && (
            <div className="text-center py-8 text-gray-400 text-sm">
              No pricing bundles available. Please contact support.
            </div>
          )}
        </div>
      )}

      {/* Packs section removed as requested */}

      {/* Coupon Code */}
      <div className="mt-6 pt-4 border-t border-gray-100">
        <label className="block text-sm font-medium text-gray-700 mb-2">Coupon Code</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={couponCode}
            onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponApplied(false); }}
            placeholder="Enter coupon code"
            className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
          />
          <motion.button
            whileHover={buttonHover}
            whileTap={buttonTap}
            onClick={applyCoupon}
            className="px-4 py-2.5 bg-gray-800 text-white rounded-lg font-medium hover:bg-gray-900"
          >
            Apply
          </motion.button>
        </div>
        {errors.coupon && <p className="text-red-500 text-xs mt-1">{errors.coupon}</p>}
        {couponApplied && <p className="text-green-600 text-xs mt-1">Coupon applied! {COUPONS[couponCode.toUpperCase()]}% discount</p>}
      </div>

      {/* Price Summary */}
      {pricing.grandTotal > 0 && (
        <div className="mt-4 p-4 bg-gray-50 rounded-xl">
          <div className="flex justify-between text-sm text-gray-600 mb-1">
            <span>Subtotal</span>
            <span>{formatCurrency(pricing.total)}</span>
          </div>
          {couponApplied && (
            <div className="flex justify-between text-sm text-green-600 mb-1">
              <span>Discount ({COUPONS[couponCode.toUpperCase()]}%)</span>
              <span>-{formatCurrency(pricing.total - pricing.discounted)}</span>
            </div>
          )}
          <div className="flex justify-between font-bold text-gray-800 pt-2 border-t border-gray-200 mt-2">
            <span>Total</span>
            <span>{formatCurrency(pricing.grandTotal)}</span>
          </div>
        </div>
      )}
    </FormCard>
    );
  };

  const renderStep6 = () => (
    <FormCard>
      <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-1 sm:mb-2">Review Your Details</h2>
      <p className="text-gray-500 text-xs sm:text-sm mb-4 sm:mb-6">Please review all information before proceeding to payment</p>

      {/* Owner Details */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-700">Owner Information</h3>
          <button onClick={() => setCurrentStep(2)} className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1">
            <FaEdit className="w-3 h-3" /> Edit
          </button>
        </div>
        <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-gray-500">Name</span><span className="font-medium">{ownerName}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Email</span><span className="font-medium">{email}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Phone</span><span className="font-medium">+91 {contactPhone || phone}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Address</span><span className="font-medium text-right max-w-[60%]">{registeredAddress}</span></div>
          {gstNumber && <div className="flex justify-between"><span className="text-gray-500">GST</span><span className="font-medium">{gstNumber}</span></div>}
          {panNumber && <div className="flex justify-between"><span className="text-gray-500">PAN</span><span className="font-medium">{panNumber}</span></div>}
          <div className="flex justify-between"><span className="text-gray-500">Partners</span><span className="font-medium">{numberOfPartners}</span></div>
        </div>
      </div>

      {/* Businesses */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-700">Businesses ({hotels.length + restaurants.length + shops.length})</h3>
          <button onClick={() => setCurrentStep(3)} className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1">
            <FaEdit className="w-3 h-3" /> Edit
          </button>
        </div>
        <div className="space-y-2">
          {hotels.map((h, i) => (
            <div key={`review-hotel-${i}`} className="bg-blue-50 rounded-lg p-3 flex items-center gap-2 text-sm">
              <FaHotel className="text-blue-600" />
              <span className="font-medium">{h.hotelName}</span>
              <span className="text-gray-500">• {h.city}, {h.state}</span>
            </div>
          ))}
          {restaurants.map((r, i) => (
            <div key={`review-rest-${i}`} className="bg-orange-50 rounded-lg p-3 flex items-center gap-2 text-sm">
              <FaUtensils className="text-orange-600" />
              <span className="font-medium">{r.name}</span>
              <span className="text-gray-500">• {r.city}, {r.state}</span>
            </div>
          ))}
          {shops.map((s, i) => (
            <div key={`review-shop-${i}`} className="bg-purple-50 rounded-lg p-3 flex items-center gap-2 text-sm">
              <FaStore className="text-purple-600" />
              <span className="font-medium">{s.shopName}</span>
              <span className="text-gray-500">• {s.city}, {s.state}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Subscription */}
      <div className="mb-2">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-700">Subscription</h3>
          <button onClick={() => setCurrentStep(4)} className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1">
            <FaEdit className="w-3 h-3" /> Edit
          </button>
        </div>
        <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
          {selectedBundles.map((bId) => {
            const b = BUNDLE_DEFINITIONS.find((bd) => bd.id === bId);
            return b ? <div key={bId} className="flex justify-between"><span className="text-gray-500">{b.name}</span><span className="font-medium">Bundle</span></div> : null;
          })}
          {planMode === "packs" && selectedPlans.map((pId) => {
            const p = pricingPlans.find((pl) => (pl.planId || pl.id) === pId);
            return p ? <div key={pId} className="flex justify-between"><span className="text-gray-500">{p.name || p.planName}</span><span className="font-medium">Pack</span></div> : null;
          })}
          {selectedDuration && (
            <div className="flex justify-between"><span className="text-gray-500">Duration</span><span className="font-medium">{selectedDuration.name || selectedDuration.durationName}</span></div>
          )}
          {couponApplied && (
            <div className="flex justify-between"><span className="text-gray-500">Coupon</span><span className="font-medium text-green-600">{couponCode} (-{COUPONS[couponCode.toUpperCase()]}%)</span></div>
          )}
          <div className="flex justify-between font-bold text-gray-800 pt-2 border-t border-gray-200 mt-2">
            <span>Grand Total</span>
            <span>{formatCurrency(pricing.grandTotal)}</span>
          </div>
        </div>
      </div>
    </FormCard>
  );

  const renderStep7 = () => (
    <FormCard>
      <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-1 sm:mb-2">Payment</h2>
      <p className="text-gray-500 text-xs sm:text-sm mb-4 sm:mb-6">Complete your payment to finish onboarding</p>

      {/* Order Summary */}
      <div className="bg-gray-50 rounded-xl p-5 mb-6">
        <h3 className="font-semibold text-gray-700 mb-3">Order Summary</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">Subtotal</span>
            <span>{formatCurrency(pricing.total)}</span>
          </div>
          {couponApplied && (
            <div className="flex justify-between text-green-600">
              <span>Discount ({COUPONS[couponCode.toUpperCase()]}%)</span>
              <span>-{formatCurrency(pricing.total - pricing.discounted)}</span>
            </div>
          )}
          <div className="flex justify-between font-bold text-lg text-gray-800 pt-3 border-t border-gray-200 mt-3">
            <span>Total Payable</span>
            <span>{formatCurrency(pricing.grandTotal)}</span>
          </div>
        </div>
      </div>

      <motion.button
        whileHover={buttonHover}
        whileTap={buttonTap}
        onClick={handlePayment}
        disabled={loading}
        className="w-full py-3.5 bg-blue-600 text-white rounded-xl font-semibold text-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
            Processing...
          </span>
        ) : (
          `Pay ${formatCurrency(pricing.grandTotal)}`
        )}
      </motion.button>

      <p className="text-center text-xs text-gray-400 mt-3">
        Secured by Razorpay. Your payment information is encrypted.
      </p>
    </FormCard>
  );

  const renderStep8 = () => (
    <FormCard>
      {paymentSuccess ? (
        <div className="text-center py-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <FaCheck className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-1 sm:mb-2">Payment Successful!</h2>
          <p className="text-gray-500 mb-4">Your onboarding is complete. Welcome to Trivana!</p>
          <div className="bg-green-50 rounded-xl p-4 mb-6 inline-block">
            <p className="text-sm text-gray-600">Payment ID</p>
            <p className="font-mono font-semibold text-green-700">{paymentId}</p>
          </div>
          <p className="text-gray-500 text-sm">
            Redirecting to merchant login in <span className="font-bold text-blue-600">{countdown}</span> seconds...
          </p>
          <div className="mt-4">
            <a
              href="https://trivanamerchant.com/login"
              className="text-blue-600 hover:underline text-sm font-medium"
            >
              Go to Login Now →
            </a>
          </div>
        </div>
      ) : (
        <div className="text-center py-8">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-3xl">✕</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-1 sm:mb-2">Payment Failed</h2>
          <p className="text-red-500 mb-6">{paymentError || "Something went wrong with your payment."}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <motion.button
              whileHover={buttonHover}
              whileTap={buttonTap}
              onClick={() => { setPaymentError(""); setCurrentStep(6); }}
              className="px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700"
            >
              Try Again
            </motion.button>
            <a
              href="mailto:support@trivana.com"
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 inline-flex items-center justify-center"
            >
              Contact Support
            </a>
          </div>
        </div>
      )}
    </FormCard>
  );

  // ─── Step Renderer Map ───────────────────────────────────────────────────────

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0: return renderStep1();
      case 1: return renderStep2();
      case 2: return renderStep3();
      case 3: return renderStep4();
      case 4: return renderStep5();
      case 5: return renderStep6();
      case 6: return renderStep7();
      case 7: return renderStep8();
      default: return null;
    }
  };

  // ─── Navigation Buttons ──────────────────────────────────────────────────────

  const showBackButton = currentStep > 1 && currentStep < 7; // never go back to step 0 (Account) once logged in
  const showNextButton = currentStep < 6 && currentStep !== 1;
  const showVerifyNextButton = currentStep === 1 && emailVerified && whatsAppVerified;

  // Gate: non-logged-in users see the sign-in prompt instead of the form
  if (!user) {
    return <OnboardingGate />;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Top Panel (Desktop) */}
      <TopPanel currentStep={currentStep} />

      {/* Mobile Step Indicator */}
      <MobileStepIndicator currentStep={currentStep} />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        <div className="flex-1 flex lg:items-center justify-center px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
          <div className="w-full max-w-lg lg:max-w-2xl xl:max-w-3xl">
            {/* Global Error */}
            {globalError && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-3 bg-red-50 border-l-4 border-red-400 text-red-700 text-sm rounded-r-lg"
              >
                {globalError}
              </motion.div>
            )}

            {/* Animated Step Content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                {renderCurrentStep()}
              </motion.div>
            </AnimatePresence>

            {/* Navigation Buttons */}
            {(showBackButton || showNextButton || showVerifyNextButton) && (
              <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3 mt-6 sm:mt-8 pb-4">
                {showBackButton ? (
                  <motion.button
                    whileHover={buttonHover}
                    whileTap={buttonTap}
                    onClick={handleBack}
                    className="w-full sm:w-auto px-6 py-3 sm:py-2.5 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors text-sm"
                  >
                    Back
                  </motion.button>
                ) : (
                  <div />
                )}

                {(showNextButton || showVerifyNextButton) && (
                  <motion.button
                    whileHover={buttonHover}
                    whileTap={buttonTap}
                    onClick={handleNext}
                    disabled={loading}
                    className="w-full sm:w-auto px-8 py-3 sm:py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                        Processing
                      </span>
                    ) : currentStep === 5 ? "Proceed to Payment" : "Continue"}
                  </motion.button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
